import { brokersService } from '@/services';
import type { Broker, GetBrokersParams } from '@/services/brokers/types';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import {
  BULK_MAX_SELECTION,
  type BulkBrokerRef,
} from '../utils/bulk-broker-products.util';

const PAGE_SIZE = 50;

export type BulkCandidateFilters = Pick<
  GetBrokersParams,
  'search' | 'advisoryId' | 'city' | 'state'
>;

function toRef(broker: Broker): BulkBrokerRef {
  const location = [broker.city, broker.state].filter(Boolean).join('/');

  return {
    code: broker.code,
    name: broker.name,
    document: broker.licenseNumber
      ? formatRegisterNumber(broker.licenseNumber)
      : undefined,
    location: location || undefined,
  };
}

async function fetchPage(filters: BulkCandidateFilters, page: number) {
  const response = await brokersService.listBrokers({
    ...filters,
    page,
    pageSize: PAGE_SIZE,
    sortField: 'PartyName',
    sortType: 'asc',
  });

  if (!response.success || !response.data) {
    throw new Error(response.error ?? 'Erro ao carregar corretores');
  }

  return response.data as unknown as {
    data: Broker[];
    meta: { total: number; totalPages: number };
  };
}

/**
 * Corretoras candidatas à operação em massa, carregadas sob demanda.
 *
 * Lista com "Carregar mais" em vez de paginação: paginar apagaria da tela quem
 * já foi marcado, e a seleção é justamente o que o admin precisa conferir antes
 * de aplicar.
 */
export function useBulkBrokerCandidates(
  filters: BulkCandidateFilters,
  enabled: boolean
) {
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: ['brokers', 'bulk-candidates', filters],
    queryFn: ({ pageParam }) => fetchPage(filters, pageParam),
    initialPageParam: 1,
    // Para no teto da seleção: além dele o índice do InsureMO devolve 500 (não
    // pagina depois de 1000 registros), e o aviso de "refine o filtro" já
    // aparece para o admin.
    getNextPageParam: (lastPage, allPages) =>
      allPages.length < (lastPage.meta?.totalPages ?? 0) &&
      (allPages.length + 1) * PAGE_SIZE <= BULK_MAX_SELECTION
        ? allPages.length + 1
        : undefined,
    enabled,
    staleTime: 1000 * 60,
  });

  const brokers = useMemo(() => {
    const pages = query.data?.pages ?? [];
    // `code` é a chave da seleção e o identificador que a Party_products usa em
    // `party_id`: sem ele a corretora não pode entrar no lote.
    return pages.flatMap(page => page.data.filter(b => b.code).map(toRef));
  }, [query.data]);

  const total = query.data?.pages[0]?.meta?.total ?? 0;

  /**
   * Busca todas as corretoras do filtro numa tacada, para o "selecionar todas".
   *
   * Fica fora do infinite query de propósito: o resultado alimenta a seleção,
   * não a lista renderizada.
   */
  const fetchAllForFilter = useCallback(async (): Promise<BulkBrokerRef[]> => {
    const response = await queryClient.fetchQuery({
      queryKey: ['brokers', 'bulk-candidates', 'all', filters],
      queryFn: async () => {
        const result = await brokersService.listBrokers({
          ...filters,
          page: 1,
          pageSize: BULK_MAX_SELECTION,
          sortField: 'PartyName',
          sortType: 'asc',
        });

        if (!result.success || !result.data) {
          throw new Error(result.error ?? 'Erro ao carregar corretores');
        }

        return result.data as unknown as { data: Broker[] };
      },
      staleTime: 1000 * 60,
    });

    return response.data.filter(b => b.code).map(toRef);
  }, [filters, queryClient]);

  return {
    brokers,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    fetchAllForFilter,
  };
}
